import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import WorkGalleryModal from './WorkGalleryModal';

const heroPoints = [
  'Create engaging animated educational content for children.',
  'Deliver bite-sized daily learning through WhatsApp.',
  'Promote African languages and culture using modern technology.',
  'Develop educational content that is simple, fun, and easy to access.',
  'Build innovative digital learning solutions for families, schools, and communities.',
];

const SLIDE_INTERVAL_MS = 3500;

const Hero = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      setActiveIndex((current) => (current + 1) % heroPoints.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section className="relative bg-gradient-to-br from-indigo-900 to-purple-800 text-white py-24 overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full opacity-10">
        <div className="absolute top-20 left-20 w-40 h-40 rounded-full bg-indigo-500 filter blur-3xl"></div>
        <div className="absolute bottom-10 right-20 w-60 h-60 rounded-full bg-purple-600 filter blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row items-center">
          <div className="lg:w-1/2 mb-16 lg:mb-0 relative z-10">
            <div className="max-w-xl">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Bring Your Stories to Life with{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-300">
                  Stunning Animations
                </span>
              </h1>
              <div
                className="mb-10 min-h-[4.5rem] sm:min-h-[3.5rem]"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
              >
                <AnimatePresence mode="wait">
                  <motion.p
                    key={activeIndex}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className="text-lg md:text-xl text-gray-200 leading-relaxed"
                    aria-live="polite"
                  >
                    {heroPoints[activeIndex]}
                  </motion.p>
                </AnimatePresence>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  type="button"
                  onClick={() => setGalleryOpen(true)}
                  className="relative overflow-hidden group bg-gradient-to-r from-yellow-400 to-yellow-300 text-gray-900 px-8 py-4 rounded-xl font-bold hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
                >
                  <span className="relative z-10">View Our Work</span>
                  <span className="absolute inset-0 bg-gradient-to-r from-yellow-500 to-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
                </button>
                <button className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 text-white px-8 py-4 rounded-xl font-bold hover:bg-opacity-20 transition-all duration-300 transform hover:-translate-y-1">
                  Get a Free Quote
                </button>
              </div>
              
              <div className="mt-12 flex flex-wrap items-center gap-6">
                <div className="flex items-center">
                  <div className="flex -space-x-2">
                    {[1, 2, 3].map((i) => (
                      <img 
                        key={i}
                        src={`https://randomuser.me/api/portraits/${i % 2 === 0 ? 'women' : 'men'}/${i+20}.jpg`}
                        alt="Happy client"
                        className="w-10 h-10 rounded-full border-2 border-white"
                      />
                    ))}
                  </div>
                  <span className="ml-3 text-sm text-gray-300">500+ Happy Clients</span>
                </div>
                <div className="flex items-center">
                  <div className="flex items-center justify-center w-10 h-10 bg-yellow-400 bg-opacity-20 rounded-full">
                    <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
                    </svg>
                  </div>
                  <span className="ml-3 text-sm text-gray-300">Award Winning</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="lg:w-1/2 relative">
            <div className="relative w-full max-w-lg mx-auto">
              <div className="absolute -top-6 -left-6 w-full h-full rounded-2xl border-2 border-yellow-400 border-opacity-40"></div>
              <div className="relative overflow-hidden rounded-2xl shadow-2xl transform rotate-1">
                <video
                  src="/1.mov"
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-auto object-cover"
                />
              </div>
              
              <div className="absolute -bottom-6 -right-6 bg-gradient-to-br from-yellow-400 to-yellow-300 w-28 h-28 rounded-full flex flex-col items-center justify-center text-gray-900 font-bold shadow-xl animate-bounce">
                <span className="text-3xl">50%</span>
                <span className="text-sm">OFF</span>
              </div>
              
              <button className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-20 h-20 bg-white bg-opacity-20 backdrop-blur-sm rounded-full flex items-center justify-center group">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <svg className="w-6 h-6 text-indigo-900 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"></path>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <WorkGalleryModal isOpen={galleryOpen} onClose={() => setGalleryOpen(false)} />
    </section>
  );
};

export default Hero;